/**
 * D2 station: G1–G4 gate screen + role dashboard figures (basis always shown).
 * Offline: enqueue to IndexedDB outbox; pending never shown as accepted.
 */
import React, { useCallback, useEffect, useState } from 'react';
import GateScreen from './GateScreen';
import { displayStatus, enqueue, listPending, replayAll } from '../offline/outbox';

const API = process.env.REACT_APP_BACKEND_URL || '';

function authHeaders() {
  const token =
    localStorage.getItem('auth_token') ||
    localStorage.getItem('token') ||
    localStorage.getItem('mobileToken') ||
    '';
  const org = localStorage.getItem('active_organization_id') || '';
  const site = localStorage.getItem('active_location_id') || '';
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
    ...(org ? { 'X-Organization-Id': org } : {}),
    ...(site ? { 'X-Location-Id': site } : {}),
  };
}

function actionForGate(g) {
  if (g === 'G1') return 'receive';
  if (g === 'G2') return 'putaway';
  if (g === 'G3') return 'issue';
  return 'produce';
}

async function postCommands(commands) {
  const r = await fetch(`${API}/api/scan/commands`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ commands }),
  });
  const body = await r.json().catch(() => ({}));
  return { status: r.status, body, ok: r.ok };
}

export default function ScanStationPage() {
  const [gate, setGate] = useState('G1');
  const [recent, setRecent] = useState([]);
  const [dash, setDash] = useState(null);
  const [error, setError] = useState('');
  const [pendingCount, setPendingCount] = useState(0);

  const refreshPending = useCallback(async () => {
    try {
      if (typeof indexedDB === 'undefined') return;
      const rows = await listPending();
      setPendingCount(rows.filter((r) => r.status === 'pending').length);
      setRecent((prev) => {
        const pendingRows = rows
          .filter((r) => r.status === 'pending')
          .map((r) => ({
            command_id: r.command_id,
            code: r.code,
            result: displayStatus(r),
            pending: true,
            status: 'pending',
          }));
        const online = prev.filter((r) => !r.pending);
        return [...pendingRows, ...online].slice(0, 20);
      });
    } catch {
      /* IndexedDB unavailable (SSR / private mode) */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/scan/dashboards/ck_manager`, {
          headers: authHeaders({ locationId: localStorage.getItem('current_location_id') || '' }),
        });
        if (!r.ok) return;
        const body = await r.json();
        if (!cancelled) setDash(body);
      } catch {
        /* offline / module off */
      }
    })();
    refreshPending();
    return () => {
      cancelled = true;
    };
  }, [refreshPending]);

  useEffect(() => {
    const onOnline = async () => {
      try {
        await replayAll(async (row) => {
          const { status, body } = await postCommands([
            {
              command_id: row.command_id,
              payload_hash: row.payload_hash,
              gate: row.gate,
              action: row.action,
              code: row.code,
              qty: row.qty,
              uom: row.uom,
              ref: row.ref,
              captured_offline: true,
            },
          ]);
          return { status, body, httpStatus: status };
        });
        await refreshPending();
      } catch {
        /* keep pending */
      }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [refreshPending]);

  const onSubmitScan = useCallback(
    async ({ gate: g, code }) => {
      setError('');
      const command_id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const payload_hash = `h-${command_id}`.slice(0, 32).padEnd(8, 'x');
      const command = {
        command_id,
        payload_hash,
        gate: g,
        action: actionForGate(g),
        code,
      };

      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        try {
          await enqueue(command);
          await refreshPending();
          return { result: 'PENDING', pending: true, code, command_id, status: 'pending' };
        } catch (e) {
          setError('Could not queue offline scan');
          return { result: 'refused', pending: false, code, command_id };
        }
      }

      try {
        const { ok, status, body } = await postCommands([command]);
        if (!ok) {
          if (status >= 500 || status === 0) {
            await enqueue(command);
            await refreshPending();
            return { result: 'PENDING', pending: true, code, command_id, status: 'pending' };
          }
          setError(body.detail || `Scan failed (${status})`);
          return { result: 'refused', pending: false, code, command_id };
        }
        const event = (body.events && body.events[0]) || { result: 'accepted', code, command_id };
        setRecent((prev) => [{ ...event, code, command_id, pending: false }, ...prev].slice(0, 20));
        return event;
      } catch {
        try {
          await enqueue(command);
          await refreshPending();
          return { result: 'PENDING', pending: true, code, command_id, status: 'pending' };
        } catch {
          setError('Network error — could not queue scan');
          return { result: 'refused', pending: false, code, command_id };
        }
      }
    },
    [refreshPending],
  );

  return (
    <div className="p-4" data-testid="scan-station-page">
      <h1 className="text-xl font-semibold mb-3">Scan station</h1>
      <div className="flex gap-2 mb-4" data-testid="scan-gate-picker">
        {['G1', 'G2', 'G3', 'G4'].map((g) => (
          <button
            key={g}
            type="button"
            data-testid={`scan-gate-${g}`}
            className={gate === g ? 'font-bold underline' : ''}
            onClick={() => setGate(g)}
          >
            {g}
          </button>
        ))}
      </div>
      {pendingCount > 0 && (
        <p data-testid="scan-pending-banner" className="mb-2 text-sm">
          {pendingCount} offline scan(s) pending — not accepted until replay succeeds
        </p>
      )}
      {dash && (
        <aside data-testid="scan-dash-ck" className="mb-4 text-sm">
          <p>
            Open holds:{' '}
            <strong>{dash.exceptions_open?.value}</strong>
            <span className="opacity-70"> · {dash.exceptions_open?.basis}</span>
            <span className="opacity-70"> · {dash.exceptions_open?.period}</span>
          </p>
          <p>
            Batches in progress:{' '}
            <strong>{dash.batches_in_progress?.value}</strong>
            <span className="opacity-70"> · {dash.batches_in_progress?.basis}</span>
          </p>
        </aside>
      )}
      {error && (
        <p role="alert" data-testid="scan-station-error">
          {error}
        </p>
      )}
      <GateScreen gate={gate} onSubmitScan={onSubmitScan} recent={recent} />
    </div>
  );
}
